import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { FiPlus, FiTrash2, FiInbox } from "react-icons/fi";
import {
  StandardInfoAtom,
  ListStandardInfoAtom,
  DetailStandardInfoAtom,
} from "../../../atoms/standardModelAtom";
import MobileSubNavigator from '../../../components/MobileSubNavigator';
import { getHomeLinks } from '../../../utils/homeLinks';
import {
  tokens,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Input,
  Label,
  Field,
  SearchInput,
  Tag,
  SideList,
  SideItem,
  EmptyState,
} from "../../../components/admin/AdminUI";

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 16px;
  background: ${tokens.color.appBg};
  color: ${tokens.color.text};
  box-sizing: border-box;
  font-family: ${tokens.font};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ScrollArea = styled.div`
  max-height: 40vh;
  overflow-y: auto;
`;

const CodeItemBtn = styled(SideItem)`
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: 1px solid
    ${(p) => (p.$active ? tokens.color.primaryRing : tokens.color.borderSubtle)};
  margin-bottom: 6px;
  padding: 12px 14px;
`;

const CodeKey = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${tokens.color.heading};
`;

const CodeMeta = styled.div`
  font-size: 13px;
  color: ${tokens.color.muted};
`;

const CodeDesc = styled.div`
  font-size: 12px;
  color: ${tokens.color.subtle};
`;

const OptionRow = styled.div`
  border: 1px solid ${tokens.color.borderSubtle};
  border-radius: ${tokens.radius.md};
  padding: 12px 14px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OptionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const OptionName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${tokens.color.heading};
`;

const OptionField = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  padding: 3px 0;

  & .k {
    color: ${tokens.color.muted};
    flex: 0 0 auto;
  }
  & .v {
    color: ${tokens.color.text};
    text-align: right;
    word-break: break-word;
  }
`;

export default function MobileStandardInfo_Detail() {
  const [codeGroupInput, setCodeGroupInput] = useRecoilState(StandardInfoAtom);
  const [selectedCodeGroup, setSelectedCodeGroup] = useRecoilState(ListStandardInfoAtom);
  const [filterValue1, setFilterValue1] = useState("");
  const [codeGroupList, setCodeGroupList] = useState([]);
  const [codeOptionList, setCodeOptionList] = useState([]);

  const [selectedCodeGroupDetails, setSelectedCodeGroupDetails] = useRecoilState(DetailStandardInfoAtom);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [optionKey, setOptionKey] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [optionDesc, setOptionDesc] = useState("");
  const [stdKey, setStdKey] = useState("");
  const [stdValue, setStdValue] = useState("");
  const [stdDescription, setStdDescription] = useState("");
  const [detailVisible, setDetailVisible] = useState(false);

  useEffect(() => {
    getItemSample();
  }, []);

  const getItemSample = async () => {
    try {
      const res = await fetch("/request/getCommonCodeGroup", {
        credentials: "include",
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCodeGroupList(data);
      } else {
        console.error("API response is not an array:", data);
      }
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdDtl = async (codeGroup) => {
    try {
      const res = await fetch(
        `/request/getStdDtl?std_group_code=${codeGroup}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setSelectedCodeGroupDetails(data);
      setSelectedDetail(null);
      setStdKey("");
      setStdValue("");
      setStdDescription("");
      setDetailVisible(true);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdOptions = async (codeGroup, std_value) => {
    try {
      const res = await fetch(
        `/request/getStdOptions?std_group_code=${codeGroup}&std_value=${std_value}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setCodeOptionList(data);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const insertStdInfo = async (codeGroupInput) => {
    try {
      const res = await fetch("/request/InsertStdInfo", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codeGroupInput }),
      });
      await res.json();
      alert(`새 코드그룹 [${codeGroupInput}] 생성`);
      getItemSample();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const deleteStdInfo = async (codeGroup) => {
    if (!window.confirm(`${codeGroup}을 정말 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch("/request/deleteStdInfo", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_code: codeGroup }),
      });
      if (!res.ok)
        throw new Error(`Server responded with status code ${res.status}`);
      await res.json();
      alert("삭제 되었습니다.");
      getItemSample();
      setSelectedCodeGroupDetails([]);
      setSelectedDetail(null);
      setDetailVisible(false);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleCodeGroupClick = (codeGroup) => {
    getStdDtl(codeGroup);
    setSelectedCodeGroup(codeGroup);
  };

  const handleDetailClick = (detail) => {
    setSelectedDetail(detail);
    setStdKey(detail.std_key);
    setStdValue(detail.std_value);
    getStdOptions(detail.std_group_code, detail.std_value);
  };

  const getFilteredRows2 = () => {
    const filterText = filterValue1.toLowerCase();
    return codeGroupList.filter((row) =>
      row.std_group_code.toLowerCase().includes(filterText)
    );
  };

  const handleInsertNewOption = async () => {
    try {
      const res = await fetch("/request/InsertStdInfoDtlOption", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_code: selectedCodeGroup,
          optionName: optionKey,
          optionValue: optionValue,
          optionDesc: optionDesc,
          target_code: stdValue,
          key: stdKey,
          value: stdValue,
          description: stdDescription,
        }),
      });
      await res.json();
      alert(`코드그룹 [${selectedCodeGroup}]에 새로운 항목이 추가되었습니다.`);
      getStdOptions(selectedCodeGroup, selectedDetail?.std_value);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const links = getHomeLinks();
  const filteredRows = getFilteredRows2();

  return (
    <MobileSubNavigator
      links={links}
      sectionTitle="표준정보 상세관리"
      backPath="/StandardInfo"
      showBackButton={true}
    >
      <Container>
        <Stack>
          {/* 그룹 리스트 섹션 */}
          <Card $pad={false}>
            <CardHeader>
              <CardTitle>그룹 리스트</CardTitle>
            </CardHeader>
            <CardBody>
              <SearchInput
                placeholder="찾을 코드를 입력해주세요."
                value={filterValue1}
                onChange={(e) => setFilterValue1(e.target.value)}
                onSearch={getFilteredRows2}
                grow="1 1 auto"
              />

              <ScrollArea style={{ marginTop: 14 }}>
                {filteredRows.length === 0 ? (
                  <EmptyState
                    icon={<FiInbox />}
                    title="코드그룹이 없습니다"
                    description="검색 조건과 일치하는 코드그룹이 없습니다."
                    pad="28px 16px"
                  />
                ) : (
                  <SideList>
                    {filteredRows.map((row, rowIndex) => (
                      <SideItem
                        key={rowIndex}
                        onClick={() => handleCodeGroupClick(row.std_group_code)}
                        $active={selectedCodeGroup === row.std_group_code}
                      >
                        {row.std_group_code}
                      </SideItem>
                    ))}
                  </SideList>
                )}
              </ScrollArea>

              <Field $mb="0" style={{ marginTop: 16 }}>
                <Label>새 코드그룹</Label>
                <Input
                  value={codeGroupInput}
                  onChange={(e) => setCodeGroupInput(e.target.value)}
                  placeholder="새 코드그룹명을 입력하세요."
                />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Button
                    $variant="dangerSoft"
                    style={{ flex: 1 }}
                    onClick={() => deleteStdInfo(selectedCodeGroup)}
                  >
                    <FiTrash2 /> 삭제
                  </Button>
                  <Button
                    $variant="primary"
                    style={{ flex: 1 }}
                    onClick={() => insertStdInfo(codeGroupInput)}
                  >
                    <FiPlus /> 추가
                  </Button>
                </div>
              </Field>
            </CardBody>
          </Card>

          {/* 코드 리스트 섹션 */}
          {detailVisible && (
            <Card $pad={false}>
              <CardHeader>
                <CardTitle>코드 리스트</CardTitle>
              </CardHeader>
              <CardBody>
                <ScrollArea style={{ maxHeight: "50vh" }}>
                  {selectedCodeGroupDetails && selectedCodeGroupDetails.length > 0 ? (
                    selectedCodeGroupDetails.map((detail, index) => (
                      <CodeItemBtn
                        key={index}
                        onClick={() => handleDetailClick(detail)}
                        $active={
                          selectedDetail &&
                          selectedDetail.std_key === detail.std_key
                        }
                      >
                        <CodeKey>{detail.std_key}</CodeKey>
                        <CodeMeta>{detail.std_value}</CodeMeta>
                        <CodeDesc>{detail.std_description}</CodeDesc>
                      </CodeItemBtn>
                    ))
                  ) : (
                    <EmptyState
                      icon={<FiInbox />}
                      title="코드가 없습니다"
                      description="이 코드그룹에 등록된 코드가 없습니다."
                      pad="28px 16px"
                    />
                  )}
                </ScrollArea>
              </CardBody>
            </Card>
          )}

          {/* 옵션 추가 섹션 */}
          {selectedDetail && (
            <Card $pad={false}>
              <CardHeader>
                <CardTitle>새로운 옵션 정보 추가</CardTitle>
              </CardHeader>
              <CardBody>
                <Field>
                  <Label>옵션</Label>
                  <Input
                    type="text"
                    placeholder="옵션"
                    value={optionKey}
                    onChange={(e) => setOptionKey(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>값</Label>
                  <Input
                    type="text"
                    placeholder="값"
                    value={optionValue}
                    onChange={(e) => setOptionValue(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>설명</Label>
                  <Input
                    type="text"
                    placeholder="설명"
                    value={optionDesc}
                    onChange={(e) => setOptionDesc(e.target.value)}
                  />
                </Field>
                <Button
                  $variant="primary"
                  style={{ width: "100%" }}
                  onClick={handleInsertNewOption}
                >
                  <FiPlus /> 추가
                </Button>

                <Label style={{ marginTop: 24, marginBottom: 12 }}>
                  등록된 상세 정보
                </Label>
                {codeOptionList && codeOptionList.length > 0 ? (
                  codeOptionList.map((option, index) => (
                    <OptionRow key={index}>
                      <OptionTitle>
                        <Tag>{option.code_group}</Tag>
                        <OptionName>{option.code_option}</OptionName>
                      </OptionTitle>
                      <OptionField>
                        <span className="k">코드</span>
                        <span className="v">{option.target_code}</span>
                      </OptionField>
                      <OptionField>
                        <span className="k">값</span>
                        <span className="v">{option.code_value}</span>
                      </OptionField>
                      <OptionField>
                        <span className="k">설명</span>
                        <span className="v">{option.code_desc}</span>
                      </OptionField>
                    </OptionRow>
                  ))
                ) : (
                  <EmptyState
                    icon={<FiInbox />}
                    title="등록된 옵션이 없습니다"
                    description="위에서 옵션을 추가해 보세요."
                    pad="28px 16px"
                  />
                )}
              </CardBody>
            </Card>
          )}
        </Stack>
      </Container>
    </MobileSubNavigator>
  );
}
